import { useEffect, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { PageHeader, Panel, Pill } from "@/components/Bits";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Check } from "@phosphor-icons/react";

export default function ParentApparel() {
  const [catalog, setCatalog] = useState([]);
  const [orders, setOrders] = useState([]);
  const [students, setStudents] = useState([]);
  const [cart, setCart] = useState({});

  const load = async () => {
    const [c, o, s] = await Promise.all([api.get("/apparel/catalog"), api.get("/apparel/orders"), api.get("/students")]);
    setCatalog(c.data); setOrders(o.data); setStudents(s.data);
  };
  useEffect(() => { load(); }, []);

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => ({ ...c, [id]: Math.max((c[id] || 0) - 1, 0) }));

  const total = Object.entries(cart).reduce((s, [id, qty]) => {
    const it = catalog.find(c => c.item_id === id);
    return s + (it ? it.price * qty : 0);
  }, 0);

  const placeOrder = async () => {
    const child = students[0];
    if (!child) return toast.error("No child linked");
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([id, q]) => {
      const it = catalog.find(c => c.item_id === id);
      return { name: it.name, size: it.sizes?.[0] || "S", qty: q, price: it.price };
    });
    if (!items.length) return;
    try { await api.post("/apparel/orders", { student_id: child.student_id, items }); toast.success("Order placed!"); setCart({}); load(); }
    catch { toast.error("Could not place"); }
  };

  return (
    <div>
      <PageHeader eyebrow="Apparel" title="Order uniforms & merch" subtitle="Right size, right time. Cared for, like everything else." />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {catalog.slice(0, 8).map(item => {
          const qty = cart[item.item_id] || 0;
          return (
            <div key={item.item_id} className="bg-white rounded-3xl border border-border p-5" data-testid={`p-item-${item.item_id}`}>
              <div className="text-5xl mb-2">{item.image}</div>
              <div className="font-heading font-bold">{item.name}</div>
              <div className="text-xs text-ink-soft mt-1 capitalize">{item.category}</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="font-heading text-xl font-bold">{formatMoney(item.price, item.currency)}</div>
                {qty === 0 ? (
                  <button onClick={() => inc(item.item_id)} className="px-4 h-9 rounded-full bg-terracotta text-white text-sm font-semibold" data-testid={`p-add-${item.item_id}`}>Add</button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => dec(item.item_id)} className="w-8 h-8 rounded-full bg-paper-alt"><Minus /></button>
                    <span className="font-semibold">{qty}</span>
                    <button onClick={() => inc(item.item_id)} className="w-8 h-8 rounded-full bg-terracotta text-white"><Plus /></button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div className="fixed bottom-24 sm:bottom-6 left-4 right-24 sm:right-24 lg:left-1/4 lg:right-[calc(25%+5rem)] z-30 bg-ink text-white rounded-3xl p-4 flex items-center justify-between shadow-floating" data-testid="cart-bar">
          <div>
            <div className="text-xs opacity-80">Cart total</div>
            <div className="font-heading font-bold text-xl">{formatMoney(total)}</div>
          </div>
          <button onClick={placeOrder} className="px-6 py-3 rounded-full bg-terracotta text-white font-semibold flex items-center gap-2" data-testid="place-order-btn">
            <Check weight="bold" /> Place order
          </button>
        </div>
      )}

      <Panel title="Your orders">
        <div className="space-y-2">
          {orders.map(o => (
            <div key={o.order_id} className="p-3 rounded-2xl border border-border flex items-center justify-between">
              <div>
                <div className="font-semibold">{o.items.map(i => i.name).join(", ")}</div>
                <div className="text-xs text-ink-soft">{new Date(o.created_at).toLocaleDateString()} · {formatMoney(o.amount, o.currency)}</div>
              </div>
              <Pill tone={o.status === "delivered" ? "sage" : "mustard"}>{o.status.replace("_", " ")}</Pill>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
